import { useEffect, useState } from 'react'

/**
 * Click a thumbnail → full-screen modal view of the image with next/prev
 * controls if multiple images were passed in. Closes on backdrop click,
 * Escape key, or the × button.
 *
 * Usage:
 *   const [lightbox, setLightbox] = useState(null)
 *   ...
 *   {photos.map((p, i) =>
 *     <img key={i} src={p.url}
 *          onClick={() => setLightbox({ urls: photos.map(x => x.url), index: i })}
 *          style={{ cursor: 'zoom-in' }} />
 *   )}
 *   {lightbox && <PhotoLightbox {...lightbox} onClose={() => setLightbox(null)} />}
 */
export default function PhotoLightbox({ urls, index = 0, onClose }) {
  const [i, setI] = useState(index)
  const count = urls?.length || 0

  // Keyboard navigation: Escape closes, arrows navigate. Attached to window
  // so the user doesn't have to click the modal first.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' && count > 1) setI(prev => (prev + 1) % count)
      else if (e.key === 'ArrowLeft' && count > 1)  setI(prev => (prev - 1 + count) % count)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [count, onClose])

  // Lock body scroll while the lightbox is open so the page behind doesn't
  // shift when the user scrolls the image.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  if (!count) return null
  const url = urls[i]

  // A note on the styles: this is inline CSS rather than a classname so the
  // component is drop-in — no global stylesheet edits needed. The z-index
  // (9999) is higher than any other overlay in the app so the lightbox always
  // wins, including over open modals if one happens to be visible.
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      {/* Close button — top right, padded away from the edge for easier
          tap targets on phones. */}
      <button
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 16,
          width: 40, height: 40, borderRadius: 20,
          background: 'rgba(255,255,255,0.15)', color: 'white',
          border: 'none', fontSize: 22, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >×</button>

      {/* Counter (only shown when >1 images) */}
      {count > 1 && (
        <div style={{
          position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
          color: 'white', fontSize: 13, background: 'rgba(0,0,0,0.4)',
          padding: '4px 10px', borderRadius: 12,
        }}>
          {i + 1} / {count}
        </div>
      )}

      {/* Prev / Next arrows (only shown when >1 images) */}
      {count > 1 && (
        <>
          <button
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); setI((i - 1 + count) % count) }}
            style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              width: 48, height: 48, borderRadius: 24,
              background: 'rgba(255,255,255,0.15)', color: 'white',
              border: 'none', fontSize: 26, cursor: 'pointer',
            }}
          >‹</button>
          <button
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); setI((i + 1) % count) }}
            style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              width: 48, height: 48, borderRadius: 24,
              background: 'rgba(255,255,255,0.15)', color: 'white',
              border: 'none', fontSize: 26, cursor: 'pointer',
            }}
          >›</button>
        </>
      )}

      {/* The image itself. object-fit:contain so tall photos don't crop
          and wide photos fit horizontally. Max dimensions leave room
          for the close / nav controls. */}
      <img
        src={url}
        alt=""
        style={{
          maxWidth: 'calc(100% - 40px)',
          maxHeight: 'calc(100vh - 80px)',
          objectFit: 'contain',
          boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
          borderRadius: 4,
        }}
      />
    </div>
  )
}
